import React from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { UserOutlined, BookOutlined, TeamOutlined } from '@ant-design/icons';
import { Row, Col, Table, Button, Tooltip, Input } from 'antd';
import { compare } from './../../utility/CompareUtils';
import './HistoryForm.less';
import { isSpecificUser } from './../../utility/AuthorityUtils';
import { textsDK } from './../../utility/LanguageUtils';
import { isIE } from './../../utility/BrowserUtils';
import moment from 'moment';

const ButtonGroup = Button.Group;

class HistoryForm extends React.Component {
  constructor(props) {
    super(props);
    this.searchInputName = React.createRef();
    this.searchInputCompany = React.createRef();
    this.state = {
      filteredInfo: null,
      sortedInfo: { columnKey: 'timestamp', order: 'descend' },
      pagination: { defaultPageSize: 50, showSizeChanger: true, size: 'small', position: 'bottom' },
      allUsersView: false,
      searchText: '',
    };
  }

  /**
   * Switches the table data to all audit history
   */
  handleSetHistoryAll = () => {
    this.setState({ allUsersView: true });
  };

  /**
   * Switches the table data to user audit history
   */
  handleSetHistoryUser = () => {
    this.setState({ allUsersView: false });
  };

  /**
   * Sets filters and sorters on table.
   * @param pagination on table
   * @param filters on table
   * @param sorter on table
   */
  handleChange = (pagination, filters, sorter) => {
    this.setState({
      filteredInfo: filters,
      sortedInfo: sorter,
    });
  };

  /**
   * Filters column values based on search input
   * @param confirm - function to confirm search input
   * @returns {Function}
   */
  handleSearch = (confirm) => () => {
    confirm();
  };

  /**
   * Sets new search value on each input made
   * @param setSelectedKeys - search value to update.
   * @returns {function(*): *}
   */
  handleSearchChange = (setSelectedKeys) => (e) => {
    return setSelectedKeys(e.target.value ? [e.target.value] : []);
  };

  /**
   * Resets column filters
   * @param clearFilters - function to reset filters on column
   * @returns {Function}
   */
  handleReset = (clearFilters) => () => {
    clearFilters();
  };

  getTableColumns(audits, state) {
    const sortedInfo = state.sortedInfo || {};
    const filteredInfo = state.filteredInfo || {};

    return this.createTableColumns(audits, sortedInfo, filteredInfo);
  }

  /**
   * Due to IE focus issue on input as subcomponent, additional focus handling must be made.
   * @param e - e is the event which holds target(input)
   */
  handleLostFocus = (e) => {
    if (isIE) {
      e.target.focus();
      e.target.selectionStart += e.target.value.length;
      e.target.selectionEnd = e.target.value.length;
    }
  };

  /**
   * Creates a search input field to filter with.
   * @param setSelectedKeys - the function to either clear or set value of the field
   * @param selectedKeys - the value of the input.
   * @param confirm - the function to confirm the input as search value
   * @param clearFilters - the function to clear filters
   * @param inputRef - the element as ref
   * @param placeholder - the placeholder for the input field
   * @returns {*}
   */
  createFilterDropDown = (
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
    inputRef,
    placeholder
  ) => {
    return (
      <div className='custom-filter-dropdown'>
        <Input
          ref={(ele) => (inputRef.current = ele)}
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={this.handleSearchChange(setSelectedKeys)}
          onPressEnter={this.handleSearch(confirm)}
          onBlur={this.handleLostFocus}
        />
        <Button type='primary' onClick={this.handleSearch(confirm)}>
          Search
        </Button>
        <Button onClick={this.handleReset(clearFilters)}>Reset</Button>
      </div>
    );
  };

  /**
   * Sets if a column should have a filter function or not.
   * @param logTypes to filter on
   * @returns {*} filters
   */
  createLogTypesFilters = (logTypes) => {
    if (logTypes.size < 2) {
      return null;
    }

    const filters = [];
    for (let i = 0; i < logTypes.size; i++) {
      const nameValue = logTypes.get(i);
      filters.push({
        text: textsDK[nameValue],
        value: nameValue,
      });
    }
    return filters;
  };

  /**
   * Sorts the date strings in ascending or descending order.
   *
   * @param a Date string in format "DD-MM-YYYY hh:mm:ss"
   * @param b Date string in format "DD-MM-YYYY hh:mm:ss"
   * @returns {number} -1, 1 or 0
   */
  compareDate(a, b) {
    return moment.utc(a, 'DD-MM-YYYY hh:mm:ss').diff(moment.utc(b, 'DD-MM-YYYY hh:mm:ss'));
  }

  formatText(text, auditType) {
    switch (auditType) {
      case 'UnpaidFee':
      case 'CompanyCreate':
      case 'CompanyEdit':
      case 'CompanyDelete':
      case 'UserCreate':
      case 'UserEdit':
      case 'UserDelete':
        return <div>{text}</div>;

      default:
        return null;
    }
  }

  /**
   * Creates table columns
   * @returns {*[]} columns
   */
  createTableColumns(audits, sortedInfo, filteredInfo) {
    const date = {
      title: 'Dato',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: '20%',
      sorter: audits.size < 2 ? false : (a, b) => this.compareDate(a.timestamp, b.timestamp),
      sortOrder: sortedInfo.columnKey === 'timestamp' && sortedInfo.order,
      render: (text, record, index) => <div>{text}</div>,
    };
    const logType = {
      title: 'Logtype',
      dataIndex: 'auditType',
      key: 'auditType',
      width: '15%',
      filters: this.createLogTypesFilters(this.props.auditTypes),
      filteredValue: filteredInfo.auditType || null,
      onFilter: (value, record) => record.auditType.toLowerCase().includes(value.toLowerCase()),
      sorter: audits.size < 2 ? false : (a, b) => compare(a.auditType, b.auditType),
      sortOrder: sortedInfo.columnKey === 'auditType' && sortedInfo.order,
      render: (text, record, index) => <div>{textsDK[text]}</div>,
    };
    const name = {
      title: 'Navn',
      dataIndex: 'username',
      key: 'username',
      width: '20%',
      sorter: audits.size < 2 ? false : (a, b) => compare(a.username, b.username),
      sortOrder: sortedInfo.columnKey === 'username' && sortedInfo.order,
      filterDropdown:
        audits.size < 2
          ? null
          : ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) =>
              this.createFilterDropDown(
                setSelectedKeys,
                selectedKeys,
                confirm,
                clearFilters,
                this.searchInputName,
                'Søg navn'
              ),
      filterIcon: (filtered) => (
        <SearchOutlined
          className={
            filtered ? 'custom-filter-dropdown-icon-active' : 'custom-filter-dropdown-icon'
          }
        />
      ),
      onFilter: (value, record) => record.username.toLowerCase().includes(value.toLowerCase()),
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setTimeout(() => {
            this.searchInputName.current.focus();
          });
        }
      },
    };
    const company = {
      title: 'Virksomhed',
      dataIndex: 'company',
      key: 'company',
      width: '20%',
      sorter: audits.size < 2 ? false : (a, b) => compare(a.company, b.company),
      sortOrder: sortedInfo.columnKey === 'company' && sortedInfo.order,
      filterDropdown:
        audits.size < 2
          ? null
          : ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) =>
              this.createFilterDropDown(
                setSelectedKeys,
                selectedKeys,
                confirm,
                clearFilters,
                this.searchInputCompany,
                'Søg virksomhed'
              ),
      filterIcon: (filtered) => (
        <SearchOutlined
          className={
            filtered ? 'custom-filter-dropdown-icon-active' : 'custom-filter-dropdown-icon'
          }
        />
      ),
      onFilter: (value, record) => record.company.toLowerCase().includes(value.toLowerCase()),
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setTimeout(() => {
            this.searchInputCompany.current.focus();
          });
        }
      },
    };
    const details = {
      title: 'Detaljer',
      dataIndex: 'details',
      key: 'details',
      width: '25%',
      sorter: audits.size < 2 ? false : (a, b) => compare(a.details, b.details),
      sortOrder: sortedInfo.columnKey === 'details' && sortedInfo.order,
      render: (text, record, index) => {
        return this.formatText(text, record.auditType);
      },
    };
    const reasons = {
      title: 'Begrundelse',
      dataIndex: 'reason',
      key: 'reason',
      width: '25%',
      sorter: audits.size < 2 ? false : (a, b) => compare(a.reasons, b.reasons),
      sortOrder: sortedInfo.columnKey === 'reasons' && sortedInfo.order,
      ellipsis: true,
      render: (text, record, index) => {
        return this.formatText(text, record.auditType);
      },
    };
    let columns = [];

    if (this.state.allUsersView) {
      columns = [date, logType, name, company, details, reasons];
    } else {
      columns = [date, logType, details, reasons];
    }

    return columns;
  }

  render() {
    const audits = this.state.allUsersView ? this.props.allAudits : this.props.userAudits;
    const columns = this.getTableColumns(audits, this.state);

    return (
      <Row>
        <Col span={24}>
          <Row>
            <Col span={12}>
              <div className={'div-title'}>
                <BookOutlined />
                <label className={'spacing-left'}>Historik</label>
              </div>
            </Col>
            <Col span={12}>
              {isSpecificUser(this.props.user, ['ADMIN', 'SUPERUSER']) ? (
                <ButtonGroup className={'float-right'}>
                  <Tooltip title='Min historik'>
                    <Button
                      className={'large-icon-button'}
                      type={!this.state.allUsersView ? 'primary' : 'default'}
                      icon={<UserOutlined />}
                      onClick={this.handleSetHistoryUser}
                    />
                  </Tooltip>
                  <Tooltip title='Alt historik'>
                    <Button
                      className={'large-icon-button'}
                      type={this.state.allUsersView ? 'primary' : 'default'}
                      icon={<TeamOutlined />}
                      onClick={this.handleSetHistoryAll}
                    />
                  </Tooltip>
                </ButtonGroup>
              ) : null}
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Table
                autoFocus={false}
                className={'spacing-top'}
                columns={columns}
                dataSource={audits.toJS()}
                rowKey={(record) => record.id}
                onChange={this.handleChange}
                pagination={this.state.pagination}
                loading={this.props.isLoading}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    );
  }
}

export default HistoryForm;
